import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { EngineService } from '@/services/engine';
import { ScoredEvent } from '@/types/event';

const RELATED_EVENTS_PAGE_SIZE = 12;
const RELATED_EVENTS_DEFAULT_THRESHOLD = 0.65;

interface UseRelatedEventsParams {
    eventId: string | undefined;
    pageSize?: number;
    threshold?: number;
    sourceId?: string[];
    folderId?: string[];
    startTime?: number;
    endTime?: number;
    classId?: number[];
    orderBy?: string;
}

export function useRelatedEvents({
    eventId,
    pageSize = RELATED_EVENTS_PAGE_SIZE,
    threshold = RELATED_EVENTS_DEFAULT_THRESHOLD,
    sourceId,
    folderId,
    startTime,
    endTime,
    classId,
    orderBy,
}: UseRelatedEventsParams) {
    const sourceIdKey = sourceId ? JSON.stringify(sourceId) : undefined;
    const folderIdKey = folderId ? JSON.stringify(folderId) : undefined;
    const classIdKey = classId ? JSON.stringify(classId) : undefined;

    const cacheKey = useMemo(() => {
        if (!eventId) return null;
        return `related-events-${eventId}-${pageSize}-${threshold}-${sourceIdKey}-${folderIdKey}-${startTime}-${endTime}-${classIdKey}-${orderBy}`;
    }, [
        eventId,
        pageSize,
        threshold,
        sourceIdKey,
        folderIdKey,
        startTime,
        endTime,
        classIdKey,
        orderBy,
    ]);

    const [additionalPages, setAdditionalPages] = useState<ScoredEvent[]>([]);
    const [hasMoreAfterLoad, setHasMoreAfterLoad] = useState<boolean | null>(
        null,
    );
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState<Error | null>(null);
    const loadMoreAbortRef = useRef<AbortController | null>(null);

    const searchRelated = useCallback(
        (excludeIds: string[]) =>
            EngineService.searchRelatedEvents(eventId ?? '', {
                size: pageSize,
                excludeIds,
                threshold,
                sourceId,
                folderId,
                startTime,
                endTime,
                classId,
                orderBy,
            }),
        [
            eventId,
            pageSize,
            threshold,
            sourceId,
            folderId,
            startTime,
            endTime,
            classId,
            orderBy,
        ],
    );

    const fetchFirstPage = useCallback(
        () => searchRelated([]),
        [searchRelated],
    );

    const {
        data: firstPage,
        error,
        isLoading,
        mutate,
    } = useSWR(cacheKey, fetchFirstPage, {
        revalidateOnFocus: false,
        revalidateIfStale: false,
        dedupingInterval: 5000,
        keepPreviousData: true,
    });

    useEffect(() => {
        setAdditionalPages([]);
        setHasMoreAfterLoad(null);
        setIsLoadingMore(false);
        setLoadMoreError(null);
        loadMoreAbortRef.current?.abort();
    }, [cacheKey]);

    const hasMore = firstPage
        ? (hasMoreAfterLoad ?? firstPage.page.totalPages > 1)
        : true;

    const allData = useMemo(
        () => [...(firstPage?.items ?? []), ...additionalPages],
        [firstPage, additionalPages],
    );

    const loadMore = useCallback(async () => {
        if (!eventId || isLoading || isLoadingMore || !hasMore) return;

        loadMoreAbortRef.current?.abort();
        const abortController = new AbortController();
        loadMoreAbortRef.current = abortController;
        setIsLoadingMore(true);

        try {
            const loadedIds = allData.map((item) => item.event.id);
            const nextPage = await searchRelated(loadedIds);
            if (abortController.signal.aborted) return;

            setAdditionalPages((previous) => [...previous, ...nextPage.items]);
            setHasMoreAfterLoad(nextPage.page.totalPages > 1);
        } catch (err) {
            if (abortController.signal.aborted) return;
            const normalizedError =
                err instanceof Error ? err : new Error('Unknown error');
            setLoadMoreError(normalizedError);
            console.error(
                'Failed to load more related events:',
                normalizedError,
            );
        } finally {
            setIsLoadingMore(false);
        }
    }, [eventId, isLoading, isLoadingMore, hasMore, searchRelated, allData]);

    const refetch = useCallback(() => {
        setAdditionalPages([]);
        setHasMoreAfterLoad(null);
        setLoadMoreError(null);
        void mutate();
    }, [mutate]);

    return {
        data: allData,
        error: error ?? loadMoreError,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        refetch,
    };
}
